import React from "react";
import PropTypes from "prop-types";

import { formatDate, renderTextEllipsis } from "../../../utils/Helpers";
import TruncatedWithTooltip from "../../TruncatedWithTooltip";
import { UITable, UIStatusTag } from "../../ui";
import EmptyEventsState from "../../Events/EmptyEventsState";

const EventsListTable = ({
  rows,
  pageSize,
  isEmpty,
  noDataDescription,
  labels,
  onRowClick,
  onCreateEvent,
}) => {
  const columns = React.useMemo(
    () => [
      {
        field: "name",
        headerName: labels.eventsColumn,
        flex: 1.4,
        minWidth: 240,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => {
          const value = row.name || "-";

          // Cut at the cell width (not a word count), with the full name in a tooltip.
          return (
            <TruncatedWithTooltip title={value}>
              <span className="MuiDataGrid-cellContent">{value}</span>
            </TruncatedWithTooltip>
          );
        },
      },
      {
        field: "event_type_label",
        headerName: labels.eventTypeColumn,
        flex: 1,
        minWidth: 160,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => {
          const text =
            row.event_type_label ||
            (row.event_type && labels.eventCategoryLabels?.[row.event_type]) ||
            row.event_type ||
            "";

          return (
            <span className="MuiDataGrid-cellContent" title={text || undefined}>
              {text ? renderTextEllipsis(text) : "-"}
            </span>
          );
        },
      },
      {
        field: "payment_period",
        headerName: labels.recurrenceColumn,
        flex: 0.85,
        minWidth: 150,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <div className="MuiDataGrid-cellContent">
            {row.payment_period ? (
              <UIStatusTag
                preset="table"
                status={row.payment_period}
                label={
                  labels.recurrency[row.payment_period] ?? row.payment_period
                }
              />
            ) : (
              "-"
            )}
          </div>
        ),
      },
      {
        field: "crew_size_plan",
        headerName: labels.crewSizeColumn,
        flex: 0.7,
        minWidth: 130,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <span className="MuiDataGrid-cellContent">
            {row.crew_size_plan?.name || "-"}
          </span>
        ),
      },
      {
        field: "event_start_at",
        headerName: labels.startDate,
        flex: 0.8,
        minWidth: 145,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <div className="MuiDataGrid-cellContent mobile-style">
            {row.event_start_at ? formatDate(row.event_start_at) : "-"}
          </div>
        ),
      },
      {
        field: "event_end_at",
        headerName: labels.endDate,
        flex: 0.8,
        minWidth: 145,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <div className="MuiDataGrid-cellContent mobile-style">
            {row.event_end_at ? formatDate(row.event_end_at) : "-"}
          </div>
        ),
      },
    ],
    [labels]
  );

  if (isEmpty) {
    return (
      <EmptyEventsState
        title={labels.emptyTitle}
        description={noDataDescription}
        actionLabel={onCreateEvent ? labels.emptyAction : undefined}
        onCreate={onCreateEvent}
      />
    );
  }

  return (
    <div className="bg-white rounded-4">
      <UITable
        rows={rows}
        columns={columns}
        contentSized
        pageSize={pageSize}
        hideFooter
        disableColumnSelector
        disableDensitySelector
        onRowClick={({ row }) => onRowClick(row)}
        getRowClassName={() => "row-hover"}
        rowHeight={72}
      />
    </div>
  );
};

EventsListTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  pageSize: PropTypes.number.isRequired,
  isEmpty: PropTypes.bool.isRequired,
  noDataDescription: PropTypes.string.isRequired,
  labels: PropTypes.shape({
    eventsColumn: PropTypes.string.isRequired,
    eventTypeColumn: PropTypes.string.isRequired,
    recurrenceColumn: PropTypes.string.isRequired,
    crewSizeColumn: PropTypes.string,
    startDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
    emptyAction: PropTypes.string.isRequired,
    emptyTitle: PropTypes.string.isRequired,
    eventIdPrefix: PropTypes.string.isRequired,
    eventCategoryLabels: PropTypes.object,
    recurrency: PropTypes.object.isRequired,
  }).isRequired,
  onRowClick: PropTypes.func.isRequired,
  onCreateEvent: PropTypes.func,
};

export default EventsListTable;
